import { getTimezone } from 'countries-and-timezones';
import countryToCurrency, { type Countries } from 'country-to-currency';

import { CURRENCY_SYMBOLS } from './RegistrationSurvey.constants';

type RegistrationSurveyDeviceCountryData = {
  timezone?: string;
  locales?: readonly string[];
};

const DEFAULT_CURRENCY_CODE = 'USD';
const LOCALE_REGION_PATTERN = /^[a-z]{2}$/i;

/**
 * Достает коды стран из browser locale.
 * Например: `ru-UA` -> `UA`, `en-GB` -> `GB`.
 *
 * @param {readonly string[]} locales - Список browser locales из `navigator.languages` / `navigator.language`
 * @returns {string[]} уникальные коды стран из locale в верхнем регистре
 */
function getCountryCodesFromLocales(locales: readonly string[] = []): string[] {
  const countryCodes = new Set<string>();

  locales.forEach((locale) => {
    const localeCountryCode = locale
      .replaceAll('_', '-')
      .split('-')
      .slice(1)
      .find((localePart) => LOCALE_REGION_PATTERN.test(localePart));

    if (localeCountryCode !== undefined) {
      countryCodes.add(localeCountryCode.toUpperCase());
    }
  });

  return [...countryCodes];
}

/**
 * Возвращает список стран, которые относятся к timezone устройства.
 * Некоторые timezone могут соответствовать нескольким странам.
 *
 * @param {string} timezone - IANA timezone устройства, например `Europe/Kyiv`
 * @returns {string[]} коды стран из `countries-and-timezones` в верхнем регистре
 */
function getCountryCodesByTimezone(timezone = ''): string[] {
  if (timezone === '') {
    return [];
  }

  try {
    return getTimezone(timezone)?.countries.map((countryCode) => countryCode.toUpperCase()) ?? [];
  } catch {
    return [];
  }
}

/**
 * Определяет вероятную страну пользователя.
 * Timezone имеет приоритет, browser locale используется для уточнения неоднозначной timezone или как fallback.
 *
 * @param {RegistrationSurveyDeviceCountryData} deviceCountryData - Timezone и browser locales устройства
 * @returns {string} код вероятной страны пользователя или пустая строка
 */
export function resolveRegistrationSurveyCountryCode({
  timezone = '',
  locales = [],
}: RegistrationSurveyDeviceCountryData): string {
  const localeCountryCodes = getCountryCodesFromLocales(locales);
  const timezoneCountryCodes = getCountryCodesByTimezone(timezone);

  if (timezoneCountryCodes.length === 1) {
    return timezoneCountryCodes[0] ?? '';
  }

  if (timezoneCountryCodes.length > 1) {
    const matchingLocaleCountryCode = localeCountryCodes.find((countryCode) =>
      timezoneCountryCodes.includes(countryCode),
    );

    if (matchingLocaleCountryCode !== undefined) {
      return matchingLocaleCountryCode;
    }

    return timezoneCountryCodes[0] ?? '';
  }

  return localeCountryCodes[0] ?? '';
}

/**
 * Собирает timezone и browser locales из API устройства.
 * Возвращает только доступные данные, если `navigator` или `Intl` недоступны.
 *
 * @returns {RegistrationSurveyDeviceCountryData} данные устройства для определения страны пользователя
 */
function getDeviceCountryData(): RegistrationSurveyDeviceCountryData {
  const locales: string[] = [];

  if (typeof navigator !== 'undefined') {
    if (Array.isArray(navigator.languages)) {
      locales.push(...navigator.languages);
    }

    if (navigator.language !== '' && !locales.includes(navigator.language)) {
      locales.push(navigator.language);
    }
  }

  if (typeof Intl === 'undefined') {
    return { locales };
  }

  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  if (timezone === '') {
    return { locales };
  }

  return { locales, timezone };
}

/**
 * Определяет валюту по стране пользователя.
 * Страну резолвим по timezone/browser locale, валюту берем из `country-to-currency` и ограничиваем валютами опросника.
 *
 * @param {RegistrationSurveyDeviceCountryData} [deviceCountryData] - Timezone и browser locales устройства
 * @returns {string} валюта по устройству пользователя или USD
 */
export function getDefaultCurrencyByDeviceCountry(
  deviceCountryData: RegistrationSurveyDeviceCountryData = getDeviceCountryData(),
): string {
  const countryCode = resolveRegistrationSurveyCountryCode(deviceCountryData);
  const normalizedCountryCode = countryCode.toUpperCase();
  const currencyCode =
    normalizedCountryCode in countryToCurrency ? countryToCurrency[normalizedCountryCode as Countries] : '';

  return Object.hasOwn(CURRENCY_SYMBOLS, currencyCode) ? currencyCode : DEFAULT_CURRENCY_CODE;
}
